import jsPDF from 'jspdf';
import { ArticleSummary } from '../types';

// COM-638 task 4: relocated verbatim from ContentArea.handleExportPdf (see
// task-3's review) so the jsPDF export survives ContentArea's deletion.
// ContentArea used to read `currentArticle`/`summaryData` off component
// state; this takes the same values as explicit arguments instead, since
// AllArticlesPage has no `currentArticle` state shape of its own to match.
//
// `updateDateFallback` deliberately is NOT a prop of ArticleSummaryPanel
// (which stays unchanged) - it's the feed article's own `updateDate`
// (FeedArticle, COM-638), passed in by the caller only for this fallback.
export interface ExportSummaryPdfArgs {
  articleTitle: string;
  articleUrl: string;
  summary: ArticleSummary | null;
  updateDateFallback?: string | null;
}

export function exportSummaryPdf({ articleTitle, articleUrl, summary, updateDateFallback }: ExportSummaryPdfArgs): void {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const marginLeft = 40;
  const marginTop = 40;
  const lineHeight = 18;
  const pageWidth = doc.internal.pageSize.getWidth();
  const maxTextWidth = pageWidth - marginLeft * 2;
  let cursorY = marginTop;

  const addWrappedText = (text: string, isBold = false) => {
    if (!text) return;
    doc.setFont('helvetica', isBold ? 'bold' : 'normal');
    const lines = doc.splitTextToSize(text, maxTextWidth);
    lines.forEach((line: string) => {
      if (cursorY > doc.internal.pageSize.getHeight() - marginTop) {
        doc.addPage();
        cursorY = marginTop;
      }
      doc.text(line, marginLeft, cursorY);
      cursorY += lineHeight;
    });
    cursorY += 6;
  };

  addWrappedText(articleTitle || 'Article Summary', true);

  if (summary?.publicationDate) {
    addWrappedText(`Publication date: ${summary.publicationDate}`);
  } else if (updateDateFallback) {
    addWrappedText(`Publication date: ${updateDateFallback}`);
  }

  if (articleUrl) {
    addWrappedText(`Source link: ${articleUrl}`);
  }

  // COM-641: field order matches ArticleSummaryPanel's AC-ordered section
  // list (summarySections.ts) - author/issuing authority, organisations
  // affected, jurisdiction, key points, actions required, important dates,
  // financial side, related documents. Empty renders "Not specified" (AC
  // wording), same fallback text the panel uses, not the old bare "—".
  // Correction: the AC treats "author or the authority that issued the
  // document" as ONE field (authorInfo) - there is no separate
  // issuingAuthority field, see the schema comment on ArticleSummary.
  addWrappedText('AI-generated summary of the article:', true);
  addWrappedText(`Author / issuing authority: ${summary?.authorInfo || 'Not specified'}`);
  addWrappedText(`Organizations: ${(summary?.organizations || []).join(', ') || 'Not specified'}`);
  addWrappedText(`Jurisdiction: ${summary?.jurisdiction || 'Not specified'}`);

  addWrappedText('The most important points:', true);
  (summary?.keyPoints?.length ? summary.keyPoints : ['Not specified']).forEach((p, idx) => addWrappedText(`${idx + 1}. ${p}`));

  addWrappedText('Actions required (if any):', true);
  (summary?.actionsRequired?.length ? summary.actionsRequired : ['Not specified']).forEach((p, idx) => addWrappedText(`${idx + 1}. ${p}`));

  addWrappedText('Important dates:', true);
  (summary?.importantDates?.length ? summary.importantDates : ['Not specified']).forEach((p, idx) => addWrappedText(`${idx + 1}. ${p}`));

  addWrappedText(`Financial side: ${summary?.financialInfo || 'Not specified'}`);

  addWrappedText('Related documents:', true);
  const relatedDocuments = summary?.relatedDocuments || [];
  if (relatedDocuments.length > 0) {
    relatedDocuments.forEach((doc, idx) => addWrappedText(`${idx + 1}. ${doc.title} (${doc.url})`));
  } else {
    addWrappedText('Not specified');
  }

  addWrappedText('Disclaimer:', true);
  addWrappedText('The content on this page is provided for general informational purposes only. It is automatically generated by an AI summarization tool and may not encompass all material information contained in the original source. While we strive for accuracy, we do not warrant or guarantee its completeness, timeliness, or suitability for any particular purpose.');
  addWrappedText('Important:', true);
  addWrappedText('• This summary is not legal, financial, or professional advice.');
  addWrappedText('• You should not act or rely solely on this information without first reviewing the full text of the relevant regulations and, if appropriate, seeking advice from qualified professionals.');
  addWrappedText('• We expressly disclaim all liability for any loss or damage arising from reliance on this summary.');

  doc.save(`${(articleTitle || 'summary').slice(0, 80)}.pdf`);
}
